import { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { Toast, useToast } from '../../hooks/useToast';

export function ToastContainer() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const { subscribe } = useToast();

  useEffect(() => {
    const unsubscribe = subscribe((toast) => {
      if (toast.message) {
        setToasts((prev) => {
          const existing = prev.find(t => t.id === toast.id);
          if (existing) {
            return prev;
          }
          return [...prev, toast];
        });
      } else {
        setToasts((prev) => prev.filter(t => t.id !== toast.id));
      }
    });

    return unsubscribe;
  }, [subscribe]);

  const getIcon = (type: Toast['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircle size={20} />;
      case 'error':
        return <AlertCircle size={20} />;
      case 'warning':
        return <AlertTriangle size={20} />;
      case 'info':
      default:
        return <Info size={20} />;
    }
  };

  const getStyles = (type: Toast['type']) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'info':
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  const getIconColor = (type: Toast['type']) => {
    switch (type) {
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      case 'warning':
        return 'text-yellow-600';
      case 'info':
      default:
        return 'text-blue-600';
    }
  };

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md pointer-events-none">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`${getStyles(
            toast.type
          )} border rounded-lg p-4 flex items-start gap-3 shadow-lg pointer-events-auto animate-in slide-in-from-right`}
        >
          <div className={`flex-shrink-0 ${getIconColor(toast.type)}`}>
            {getIcon(toast.type)}
          </div>
          <div className="flex-1">
            <p className="font-medium text-sm">{toast.message}</p>
          </div>
          <button
            onClick={() => {
              setToasts((prev) => prev.filter(t => t.id !== toast.id));
            }}
            className="flex-shrink-0 opacity-50 hover:opacity-100 transition"
          >
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}